import { Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, scan } from 'rxjs';
import { UrlState } from '../../interfaces/url-state.inteface';
import { isValidUrl } from '../../validators/url.validator';
import { ScrollService } from '../scroll-service/scroll.service';
import { ToastMessageService } from '../toast-message.service';
import { TranslateService } from '@ngx-translate/core';

const HTTPS_PROTOCOL_PREFIX = 'https://';

@Injectable({
  providedIn: 'root',
})
export class UrlService {
  private _previousUrl = signal<string | undefined>(undefined);
  private _currentUrl = signal<string | undefined>(undefined);
  public previousUrl = this._previousUrl.asReadonly();
  public currentUrl = this._currentUrl.asReadonly();

  constructor(
    private toastMessageService: ToastMessageService,
    private scrollService: ScrollService,
    private router: Router,
    private readonly translate: TranslateService,
  ) {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        scan<NavigationEnd, UrlState>(
          (state, event) => ({
            previous: state.current,
            current: event.urlAfterRedirects,
          }),
          {
            previous: undefined,
            current: undefined,
          },
        ),
      )
      .subscribe((state) => {
        this._previousUrl.set(state.previous);
        this._currentUrl.set(state.current);
      });
  }

  openURL(
    _link?: string,
    _config: { target?: string; features?: string; isSecure?: boolean } = {},
  ): void {
    if (!_link) {
      // no url -> url invalid -> notify
      this.notifyUrlError();
      return;
    }

    const url = this.getValidatedUrl(_link, _config.isSecure);

    if (url) {
      // valid url -> open it
      window.open(url, _config.target ?? '_blank', _config.features);
    } else {
      // url invalid -> notify
      this.notifyUrlError();
    }
  }

  getUrlSegments(url: string): string[] {
    return url.split('?')[0].split('/').filter(Boolean);
  }

  private getValidatedUrl(link: string, isSecure?: boolean): string | null {
    if (isSecure) {
      // is secure -> just return as-is
      return link;
    }

    let url = link;

    // invalid url -> maybe it helps to add https protocol as a prefix
    if (!isValidUrl(url)) {
      url = HTTPS_PROTOCOL_PREFIX + url;
    }

    return isValidUrl(url) ? url : null;
  }

  private notifyUrlError(): void {
    this.toastMessageService.openToastDialog({
      primaryMessage: this.translate.instant('common.toast.url-open-error-title'),
      secondMessage: this.translate.instant('common.toast.url-open-error-desc'),
    });
  }

  openAppDetail(_link?: string) {
    this.scrollService.savePosition(this.router.url);

    if (_link) {
      window.open(_link, '_blank');
    }
  }
}
